import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, ApiError, streamChat } from './api';
import { AccessGate } from './components/AccessGate';
import { Cart } from './components/Cart';
import { ProductGrid } from './components/ProductGrid';
import { TechnicalPanel } from './components/TechnicalPanel';
import type { AgentEvent, CartView, ChatEntry, ProductCardData, SessionState } from './types';

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 40;

const GREETING: ChatEntry = {
  id: 'greeting',
  role: 'assistant',
  content:
    'Moin. Ich finde echte Paulmann-Artikel im Staging-Shop und stelle einen Warenkorb zusammen. Sag mir, wonach du suchst — zum Beispiel „Deckenleuchte" oder „LED Panel".',
};

export function App() {
  const [access, setAccess] = useState<boolean | null>(null);
  const [session, setSession] = useState<SessionState | null>(null);
  const [cart, setCart] = useState<CartView | null>(null);
  const [entries, setEntries] = useState<ChatEntry[]>([]);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<{ kind: 'error' | 'info'; text: string } | null>(null);
  const [cartCollapsed, setCartCollapsed] = useState(true);

  const logRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const stickChatToBottomRef = useRef(true);

  // ---- bootstrap ---------------------------------------------------------
  useEffect(() => {
    api
      .accessState()
      .then((state) => setAccess(state.granted))
      .catch(() => setAccess(false));
  }, []);

  const loadSession = useCallback(async () => {
    try {
      const state = await api.session();
      setSession(state);
      setCart(state.cart);

      const restored: ChatEntry[] = state.messages.map((message) => {
        const event = message.payload?.event;
        return {
          id: message.id,
          role: message.role,
          content: message.content,
          products: event && event.type === 'products' ? event.products : undefined,
        };
      });
      setEntries(restored.length > 0 ? restored : [GREETING]);
      setBanner(null);
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 401) {
        setAccess(false);
        return;
      }
      setBanner({
        kind: 'error',
        text: cause instanceof Error ? cause.message : 'Sitzung konnte nicht geladen werden.',
      });
    }
  }, []);

  useEffect(() => {
    if (access) void loadSession();
  }, [access, loadSession]);

  useEffect(() => {
    const log = logRef.current;
    if (!log) return;

    const handleScroll = () => {
      const distanceFromBottom = log.scrollHeight - log.scrollTop - log.clientHeight;
      stickChatToBottomRef.current = distanceFromBottom < 48;
    };

    log.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => log.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const log = logRef.current;
    if (!log || !stickChatToBottomRef.current) return;

    log.scrollTo({ top: log.scrollHeight, behavior: 'smooth' });
  }, [entries]);

  // ---- payment wait state ------------------------------------------------
  const pendingPayment =
    cart?.state === 'payment_pending' || cart?.state === 'payment_confirmed_order_pending';

  useEffect(() => {
    if (!pendingPayment) return;

    let attempts = 0;
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { cart: next } = await api.paymentStatus();
        if (cancelled) return;
        setCart(next);
        if (next.orderNumber || next.state === 'payment_failed' || next.state === 'escalation') return;
      } catch {
        /* keep waiting; a transient read failure is not a payment failure */
      }
      if (attempts < POLL_MAX_ATTEMPTS) {
        window.setTimeout(tick, POLL_INTERVAL_MS);
      } else if (!cancelled) {
        setBanner({
          kind: 'info',
          text: 'Der Zahlungsstatus steht noch aus. Lade die Seite später erneut — die Sitzung bleibt erhalten.',
        });
      }
    };

    const timer = window.setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [pendingPayment]);

  // Returning from the payment page: read the authoritative status, never trust
  // the redirect itself.
  useEffect(() => {
    if (window.location.pathname === '/zahlung/rueckkehr') {
      window.history.replaceState({}, '', '/');
      setBanner({ kind: 'info', text: 'Zurück aus der Testzahlung. Der Status wird beim Shop abgefragt.' });
      if (access) void loadSession();
    }
  }, [access, loadSession]);

  // ---- actions -----------------------------------------------------------
  const run = useCallback(
    async (action: () => Promise<{ cart: CartView }>) => {
      setBusy(true);
      setBanner(null);
      try {
        const { cart: next } = await action();
        setCart(next);
        return next;
      } catch (cause) {
        setBanner({
          kind: 'error',
          text: cause instanceof Error ? cause.message : 'Aktion fehlgeschlagen.',
        });
        return null;
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  const send = async (event?: React.FormEvent) => {
    event?.preventDefault();
    const message = draft.trim();
    if (message.length === 0 || streaming) return;

    setDraft('');
    setStreaming(true);
    setBanner(null);

    const userEntry: ChatEntry = { id: `u-${Date.now()}`, role: 'user', content: message };
    const replyId = `a-${Date.now()}`;
    setEntries((current) => [
      ...current,
      userEntry,
      { id: replyId, role: 'assistant', content: 'Der Assistent arbeitet …', pending: true },
    ]);

    const controller = new AbortController();
    abortRef.current = controller;

    let text = '';
    let products: ProductCardData[] | undefined;

    const apply = (patch: Partial<ChatEntry>) => {
      setEntries((current) =>
        current.map((entry) => (entry.id === replyId ? { ...entry, ...patch } : entry)),
      );
    };

    try {
      await streamChat(
        message,
        (agentEvent: AgentEvent) => {
          switch (agentEvent.type) {
            case 'text':
              text = text.length > 0 ? `${text}\n\n${agentEvent.text}` : agentEvent.text;
              apply({ content: text, pending: false });
              break;
            case 'tool_start':
              if (text.length === 0) apply({ content: 'Frage den Shop ab …', pending: true });
              break;
            case 'products':
              products = agentEvent.products;
              apply({ products, pending: false });
              break;
            case 'cart':
            case 'summary':
              setCart(agentEvent.cart);
              if (agentEvent.type === 'summary') setCartCollapsed(false);
              break;
            case 'error':
              setBanner({ kind: 'error', text: agentEvent.message });
              break;
            default:
              break;
          }
        },
        controller.signal,
      );
      if (text.length === 0 && !products) {
        apply({ content: 'Keine Antwort erhalten. Bitte erneut versuchen.', pending: false });
      }
    } catch (cause) {
      if (!controller.signal.aborted) {
        apply({
          content: cause instanceof Error ? cause.message : 'Verbindung zum Assistenten verloren.',
          pending: false,
        });
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const newDemo = async () => {
    abortRef.current?.abort();
    setBusy(true);
    try {
      // Starts a fresh session; completed orders and paid sessions stay untouched.
      await api.newSession();
      setEntries([GREETING]);
      await loadSession();
    } finally {
      setBusy(false);
    }
  };

  const limits = useMemo(() => session?.limits, [session]);

  if (access === null) return <p className="empty">Lade Demo …</p>;
  if (!access) return <AccessGate onGranted={() => setAccess(true)} />;

  return (
    <>
      <p className="staging-banner" role="note">
        <strong>Staging-Demo</strong> — echte Shopdaten, simulierte Testzahlung. Es entsteht keine
        reale Geldbewegung und keine Produktivbestellung.
      </p>

      <header className="app-header">
        <h1>
          Paulmann <span>Einkaufsassistent</span>
        </h1>
        <div className="header-meta">
          {session && <span>Modell: {session.modelProvider}</span>}
          {limits && (
            <span>
              Limits: {limits.distinctItems} Positionen, {limits.quantityPerItem} Stück,{' '}
              {(limits.orderTotalCents / 100).toFixed(0)} EUR
            </span>
          )}
          <button className="btn subtle" type="button" onClick={newDemo} disabled={busy}>
            Neue Demo
          </button>
        </div>
      </header>

      {banner && (
        <p className={`notice ${banner.kind}`} role={banner.kind === 'error' ? 'alert' : 'status'}>
          {banner.text}
        </p>
      )}

      <button
        className="btn ghost cart-toggle"
        type="button"
        style={{ margin: '0.75rem 1rem 0' }}
        onClick={() => setCartCollapsed((value) => !value)}
        aria-expanded={!cartCollapsed}
      >
        {cartCollapsed ? 'Warenkorb anzeigen' : 'Warenkorb ausblenden'}
        {cart && cart.items.length > 0 ? ` (${cart.items.length})` : ''}
      </button>

      <main className="layout">
        <section className="panel chat" aria-labelledby="chat-heading">
          <h2 id="chat-heading">Chat</h2>
          <div className="chat-log" ref={logRef} aria-live="polite" aria-atomic="false">
            {entries.map((entry) => (
              <div key={entry.id}>
                {entry.content && (
                  <div
                    className={`bubble ${entry.role}${entry.pending ? ' pending' : ''}`}
                  >
                    {entry.content}
                  </div>
                )}
                {entry.products && (
                  <ProductGrid
                    products={entry.products}
                    disabled={busy || Boolean(cart?.locked)}
                    onAdd={(productId, quantity) => void run(() => api.addItem(productId, quantity))}
                  />
                )}
              </div>
            ))}
          </div>

          <form className="composer" onSubmit={send}>
            <label className="visually-hidden" htmlFor="chat-input">
              Nachricht an den Einkaufsassistenten
            </label>
            <input
              id="chat-input"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  void send();
                }
              }}
              placeholder="z. B. Zeig mir eine Deckenleuchte für das Bad"
              autoComplete="off"
              disabled={streaming}
            />
            <button className="btn" type="submit" disabled={streaming || draft.trim().length === 0}>
              {streaming ? 'Sende …' : 'Senden'}
            </button>
          </form>

          <TechnicalPanel />
        </section>

        <div className="cart-column" data-collapsed={cartCollapsed}>
          {cart && (
            <Cart
              cart={cart}
              busy={busy}
              onQuantity={(productId, quantity) =>
                void run(() => api.setQuantity(productId, quantity))
              }
              onRemove={(productId) => void run(() => api.removeItem(productId))}
              onBuyer={(payload) => void run(() => api.setBuyer(payload))}
              onConfirm={() => {
                if (cart.cartVersion) void run(() => api.confirm(cart.cartVersion!));
              }}
              onPay={async () => {
                const next = await run(() => api.startPayment());
                // The shop decides where the payment happens; we only follow.
                if (next?.paymentRedirectUrl) window.location.assign(next.paymentRedirectUrl);
              }}
              onCancel={() => void run(() => api.cancel())}
            />
          )}
        </div>
      </main>
    </>
  );
}
